import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';

export default function Funnels({ api }) {
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [funnelStats, setFunnelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  
  const [newFunnel, setNewFunnel] = useState({
    name: '',
    type: '',
    description: '',
    stages: ['']
  });

  const [editFunnel, setEditFunnel] = useState({
    id: '',
    name: '',
    type: '',
    description: '',
    stages: ['']
  });

  const funnelTypes = [
    { id: 'b2b', label: 'B2B - Vendas Corporativas', description: 'Ciclo longo, alto valor' },
    { id: 'b2c', label: 'B2C - Vendas Diretas', description: 'Ciclo rápido, alto volume' },
    { id: 'pos_venda', label: 'Pós-Venda', description: 'Onboarding, upsell, renovação' },
    { id: 'marketing', label: 'Marketing', description: 'Geração e nutrição de leads' },
    { id: 'custom', label: 'Personalizado', description: 'Funil customizado' }
  ];

  const defaultStagesByType = {
    'b2b': ['Lead Gerado', 'Qualificação', 'Contato Realizado', 'Diagnóstico', 'Proposta Enviada', 'Negociação', 'Fechou', 'Perdido'],
    'b2c': ['Interesse', 'Contato', 'Apresentação', 'Negociação', 'Fechou', 'Perdido'],
    'pos_venda': ['Onboarding', 'Ativo', 'Engajado', 'Upsell', 'Renovação', 'Churn'],
    'marketing': ['Visitante', 'Lead', 'MQL', 'SQL', 'Oportunidade', 'Cliente'],
    'custom': ['Estágio 1']
  };

  useEffect(() => {
    fetchFunnels();
  }, []);

  const fetchFunnels = async () => {
    try {
      const response = await api.get('/funnels');
      // Handle both array and object with items structure
      if (Array.isArray(response.data)) {
        setFunnels(response.data);
      } else if (response.data?.items) {
        setFunnels(response.data.items);
      } else {
        setFunnels([]);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para gerenciar funis');
      } else {
        toast.error('Erro ao carregar funis');
      }
      console.error(error);
      setFunnels([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchFunnelStats = async (funnelId) => {
    try {
      const response = await api.get(`/funnels/${funnelId}/stats`);
      setFunnelStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas do funil');
      console.error(error);
    }
  };

  const handleAddFunnel = async () => {
    if (!newFunnel.name.trim() || !newFunnel.type) {
      toast.error('Nome e tipo do funil são obrigatórios');
      return;
    }

    const validStages = newFunnel.stages.filter(stage => stage.trim());
    if (validStages.length === 0) {
      toast.error('Pelo menos um estágio é obrigatório');
      return;
    }

    try {
      await api.post('/funnels', {
        ...newFunnel,
        stages: validStages
      });
      
      toast.success('Funil criado com sucesso!');
      setShowAddModal(false);
      resetNewFunnel();
      fetchFunnels();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar funil';
      toast.error(message);
    }
  };

  const handleDeleteFunnel = async (funnelId, funnelName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o funil "${funnelName}"?`)) return;

    try {
      await api.delete(`/funnels/${funnelId}`);
      toast.success('Funil excluído com sucesso!');
      fetchFunnels();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir funil';
      toast.error(message);
    }
  };

  const handleResolveOrphanedFunnels = async () => {
    try {
      setLoading(true);
      const response = await api.post('/pipelines/resolve-orphaned-funnels');
      const result = response.data;
      
      if (result.created_count > 0) {
        toast.success(`✅ Sucesso! ${result.created_count} pipelines criadas para funis órfãos`);
      } else {
        toast.info('✨ Todos os funis já possuem pipeline associada!');
      }
      
      if (result.error_count > 0) {
        toast.error(`⚠️ ${result.error_count} erros encontrados ao processar alguns funis`);
      }
      
      // Refresh the funnels list
      fetchFunnels();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao resolver funis órfãos';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const resetNewFunnel = () => {
    setNewFunnel({
      name: '',
      type: '',
      description: '',
      stages: ['']
    });
  };

  const handleTypeChange = (type) => {
    setNewFunnel({
      ...newFunnel,
      type: type,
      stages: defaultStagesByType[type] || ['']
    });
  };

  const addStage = () => {
    if (newFunnel.stages.length < 8) {
      setNewFunnel({
        ...newFunnel,
        stages: [...newFunnel.stages, '']
      });
    } else {
      toast.error('Máximo 8 estágios por funil');
    }
  };

  const removeStage = (index) => {
    if (newFunnel.stages.length > 1) {
      const updatedStages = newFunnel.stages.filter((_, i) => i !== index);
      setNewFunnel({
        ...newFunnel,
        stages: updatedStages
      });
    }
  };

  const updateStage = (index, value) => {
    const updatedStages = [...newFunnel.stages];
    updatedStages[index] = value;
    setNewFunnel({
      ...newFunnel,
      stages: updatedStages
    });
  };

  // Edit funnel functions
  const handleEditFunnel = (funnel) => {
    setEditFunnel({
      id: funnel.id,
      name: funnel.name,
      type: funnel.type,
      description: funnel.description || '',
      stages: [...funnel.stages]
    });
    setShowEditModal(true);
  };

  const handleSaveEditFunnel = async () => {
    if (!editFunnel.name.trim() || !editFunnel.type) {
      toast.error('Nome e tipo do funil são obrigatórios');
      return;
    }

    const validStages = editFunnel.stages.filter(stage => stage.trim());
    if (validStages.length === 0) {
      toast.error('Pelo menos um estágio é obrigatório');
      return;
    }

    try {
      await api.put(`/funnels/${editFunnel.id}`, {
        name: editFunnel.name,
        type: editFunnel.type,
        description: editFunnel.description,
        stages: validStages
      });
      
      toast.success('Funil atualizado com sucesso!');
      setShowEditModal(false);
      resetEditFunnel();
      fetchFunnels();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao atualizar funil';
      toast.error(message);
    }
  };

  const resetEditFunnel = () => {
    setEditFunnel({
      id: '',
      name: '',
      type: '',
      description: '',
      stages: ['']
    });
  };

  const handleEditTypeChange = (type) => {
    setEditFunnel({
      ...editFunnel,
      type: type,
      stages: defaultStagesByType[type] || ['']
    });
  };

  const addEditStage = () => {
    if (editFunnel.stages.length < 8) {
      setEditFunnel({
        ...editFunnel,
        stages: [...editFunnel.stages, '']
      });
    } else {
      toast.error('Máximo 8 estágios por funil');
    }
  };

  const removeEditStage = (index) => {
    if (editFunnel.stages.length > 1) {
      const updatedStages = editFunnel.stages.filter((_, i) => i !== index);
      setEditFunnel({
        ...editFunnel,
        stages: updatedStages
      });
    }
  };

  const updateEditStage = (index, value) => {
    const updatedStages = [...editFunnel.stages];
    updatedStages[index] = value;
    setEditFunnel({
      ...editFunnel,
      stages: updatedStages
    });
  };

  const getTypeColor = (type) => {
    const colors = {
      'b2b': 'bg-blue-100 text-blue-800 border-blue-200',
      'b2c': 'bg-green-100 text-green-800 border-green-200',
      'pos_venda': 'bg-purple-100 text-purple-800 border-purple-200',
      'marketing': 'bg-orange-100 text-orange-800 border-orange-200',
      'custom': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTypeLabel = (type) => {
    const typeObj = funnelTypes.find(t => t.id === type);
    return typeObj ? typeObj.label : type;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="funnels-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Gestão de Funis</h1>
          <p className="text-slate-600 mt-1">Configure funis de vendas para diferentes tipos de negócio</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Button to resolve orphaned funnels */}
          <Button 
            variant="outline" 
            onClick={handleResolveOrphanedFunnels}
            disabled={loading}
            className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
            title="Criar pipelines para funis que não possuem pipeline associada"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Resolver Órfãos
          </Button>
          
          {/* Add funnel button */}
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-funnel-button">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Novo Funil
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Criar Novo Funil</DialogTitle>
              <DialogDescription>Configure um novo funil de vendas com estágios personalizados</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="funnel-name">Nome do Funil *</Label>
                  <Input
                    id="funnel-name"
                    data-testid="funnel-name-input"
                    value={newFunnel.name}
                    onChange={(e) => setNewFunnel({...newFunnel, name: e.target.value})}
                    placeholder="Ex: Vendas Enterprise"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="funnel-type">Tipo de Funil *</Label>
                  <Select value={newFunnel.type} onValueChange={handleTypeChange}>
                    <SelectTrigger data-testid="funnel-type-select">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {funnelTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-slate-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="funnel-description">Descrição</Label>
                <Textarea
                  id="funnel-description"
                  value={newFunnel.description}
                  onChange={(e) => setNewFunnel({...newFunnel, description: e.target.value})}
                  placeholder="Descreva o propósito deste funil..."
                  rows={3}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Estágios do Funil</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addStage}
                    disabled={newFunnel.stages.length >= 8}
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Adicionar Estágio
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {newFunnel.stages.map((stage, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center text-xs">
                        {index + 1}
                      </Badge>
                      <Input
                        value={stage}
                        onChange={(e) => updateStage(index, e.target.value)}
                        placeholder={`Estágio ${index + 1}`}
                        className="flex-1"
                      />
                      {newFunnel.stages.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeStage(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Máximo 8 estágios por funil</p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddFunnel} className="btn-primary">
                Criar Funil
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Funnels List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {funnels.length === 0 ? (
          <div className="col-span-full">
            <Card className="glass border-white/20">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum funil encontrado</h3>
                <p className="text-slate-500">Crie seu primeiro funil de vendas</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          funnels.map((funnel) => (
            <Card key={funnel.id} className="glass border-white/20 card-hover">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl text-slate-900 mb-2">{funnel.name}</CardTitle>
                    <Badge className={getTypeColor(funnel.type)}>
                      {getTypeLabel(funnel.type)}
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditFunnel(funnel)}
                      className="bg-white/60"
                      title="Editar funil"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedFunnel(funnel);
                        setShowStatsModal(true);
                        fetchFunnelStats(funnel.id);
                      }}
                      className="bg-white/60"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteFunnel(funnel.id, funnel.name)}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </Button>
                  </div>
                </div>
                {funnel.description && (
                  <CardDescription>{funnel.description}</CardDescription>
                )}
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      Estágios ({funnel.stages.length})
                    </p>
                    <div className="space-y-2">
                      {funnel.stages.map((stage, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <Badge variant="outline" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                            {index + 1}
                          </Badge>
                          <span className="text-slate-600">{stage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-500 border-t pt-3">
                    Criado em {new Date(funnel.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Estatísticas do Funil</DialogTitle>
            <DialogDescription>
              {selectedFunnel?.name} - Análise de performance
            </DialogDescription>
          </DialogHeader>
          
          {funnelStats && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-600">{funnelStats.total_leads}</div>
                  <div className="text-sm text-slate-600">Total de Leads</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-600">{funnelStats.conversion_rate}%</div>
                  <div className="text-sm text-slate-600">Taxa de Conversão</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-purple-600">{funnelStats.stage_stats?.length || 0}</div>
                  <div className="text-sm text-slate-600">Estágios Ativos</div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-slate-800">Distribuição por Estágio</h4>
                {funnelStats.stage_stats?.map((stage, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium">{stage.stage}</span>
                      <span className="text-slate-500">{stage.count} leads ({stage.percentage}%)</span>
                    </div>
                    <Progress value={stage.percentage} className="h-2" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Funnel Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Funil</DialogTitle>
            <DialogDescription>Modifique as configurações do funil selecionado</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-funnel-name">Nome do Funil *</Label>
                <Input
                  id="edit-funnel-name"
                  value={editFunnel.name}
                  onChange={(e) => setEditFunnel({...editFunnel, name: e.target.value})}
                  placeholder="Ex: Vendas Enterprise"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-funnel-type">Tipo de Funil *</Label>
                <Select value={editFunnel.type} onValueChange={handleEditTypeChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {funnelTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div>
                          <div className="font-medium">{type.label}</div>
                          <div className="text-xs text-slate-500">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-funnel-description">Descrição</Label>
              <Textarea
                id="edit-funnel-description"
                value={editFunnel.description}
                onChange={(e) => setEditFunnel({...editFunnel, description: e.target.value})}
                placeholder="Descreva o objetivo deste funil"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Estágios do Funil *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addEditStage}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Adicionar Estágio
                </Button>
              </div>
              <div className="space-y-3">
                {editFunnel.stages.map((stage, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <Input
                      value={stage}
                      onChange={(e) => updateEditStage(index, e.target.value)}
                      placeholder={`Estágio ${index + 1}`}
                      className="flex-1"
                    />
                    {editFunnel.stages.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeEditStage(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">Máximo 8 estágios por funil</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEditFunnel} className="btn-primary">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}